import { useEffect, useState } from "react";
import { Banner, SimpleTable, StatGrid, StatTile } from "@NP-Webber/crm-kit/dist/next/index.js";
import { aiApi, type AiUsageDto } from "../../api/aiApi";

type UsageRow = AiUsageDto["users"][number];

const usd = (n: number) => `$${n.toLocaleString("he-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n: number) => n.toLocaleString("he-IL");

/** «צריכת AI» — this month's assistant usage and cost per user (ai.usage.view). */
export function AiUsagePanel() {
  const [data, setData] = useState<AiUsageDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    aiApi
      .usage()
      .then(setData)
      .catch((e: unknown) => setError((e as { message?: string })?.message || "לא הצלחנו לטעון את נתוני הצריכה"));
  }, []);

  if (error) return <Banner tone="danger">{error}</Banner>;
  const requests = data?.users.reduce((s, u) => s + u.requests, 0) ?? 0;
  const used = data && data.budgetUsd ? Math.round((data.totalUsd / data.budgetUsd) * 100) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <StatGrid min={160}>
        <StatTile label="עלות החודש" icon="coin" tone="primary" value={data ? usd(data.totalUsd) : undefined} loading={!data} />
        <StatTile
          label="תקציב חודשי"
          icon="wallet"
          tone={used !== null && used >= 90 ? "danger" : "neutral"}
          value={data ? (data.budgetUsd ? usd(data.budgetUsd) : "ללא הגבלה") : undefined}
          sub={used !== null ? `${used}% נוצל` : "AI_MONTHLY_BUDGET_USD בשרת"}
          loading={!data}
        />
        <StatTile label="פניות למודל" icon="sparkle" tone="violet" value={data ? num(requests) : undefined} loading={!data} />
      </StatGrid>
      <SimpleTable
        ariaLabel="צריכת AI לפי משתמש"
        loading={!data}
        rows={data?.users ?? []}
        rowKey={(r: UsageRow) => r.userId ?? "deleted"}
        empty="עוד לא היה שימוש בעוזר החודש"
        columns={[
          { key: "displayName", label: "משתמש", render: (r: UsageRow) => r.displayName ?? "משתמש שנמחק" },
          { key: "requests", label: "פניות", align: "end", render: (r: UsageRow) => num(r.requests) },
          { key: "inputTokens", label: "טוקנים נכנסים", align: "end", render: (r: UsageRow) => num(r.inputTokens) },
          { key: "outputTokens", label: "טוקנים יוצאים", align: "end", render: (r: UsageRow) => num(r.outputTokens) },
          { key: "costUsd", label: "עלות", align: "end", render: (r: UsageRow) => usd(r.costUsd) },
        ]}
      />
      {data ? (
        <small style={{ color: "var(--ck-text-3)" }}>
          מתחילת החודש ({new Date(data.since).toLocaleDateString("he-IL")}) · מחיר לפי מחירון הספק
        </small>
      ) : null}
    </div>
  );
}
